import { useMutation, useQueryClient } from '@tanstack/react-query';

import { engineService } from '@/services/engine-service';
import type { Evaluator, WorkflowRule } from '@/types/core/workflow-rule';
import { pollUntilResourceNewerThan } from '@/utils/poll';
import type { UUID } from '@/utils/uuid';

import { engineQueryKeys, evaluatorMutationKeys } from './query-keys';

/**
 * Hook to update an evaluator.
 * Polls until the read model reflects a newer _rev before resolving.
 */
export function useUpdateEvaluator(tenantId?: UUID, ruleId?: UUID) {
  const qc = useQueryClient();

  return useMutation({
    mutationKey: evaluatorMutationKeys.updateEvaluator(tenantId, ruleId),
    mutationFn: async (
      updates: Partial<Omit<WorkflowRule, 'ruleId' | 'tenantId' | 'type'>>
    ) => {
      if (!tenantId) throw new Error('Missing tenant id');
      if (!ruleId) throw new Error('Missing rule id');

      const cached = qc.getQueryData<Evaluator | null>(
        engineQueryKeys.evaluatorDetail(tenantId, ruleId)
      );
      const minRev = cached?._rev ?? -2;

      await engineService.updateEvaluator(tenantId, ruleId, updates);

      return pollUntilResourceNewerThan(
        () => engineService.findEvaluator(tenantId, ruleId),
        minRev,
        { maxAttempts: 10, delayMs: 300 }
      );
    },
    onSuccess: () => {
      qc.invalidateQueries({
        queryKey: engineQueryKeys.evaluatorListByTenant(tenantId),
        refetchType: 'all',
      });
      qc.invalidateQueries({
        queryKey: engineQueryKeys.evaluatorDetail(tenantId, ruleId),
      });
    },
  });
}
